'use client';

import { FormField } from '@/features/forms/FormPrimitives';
import readHubMessage from '@/features/hub/shared/readHubMessage';
import { clientFetch } from '@/lib/api/clientFetch';
import { useEffect, useMemo, useState } from 'react';

function formatRequestTime(value, locale, fallbackLabel) {
  if (!value) {
    return fallbackLabel;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return fallbackLabel;
  }

  try {
    return new Intl.DateTimeFormat(locale || 'en', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
}

function getStatusTone(status) {
  if (status === 'approved') {
    return ' hub-pill--accent';
  }

  if (status === 'pending') {
    return ' hub-pill--warning';
  }

  return '';
}

export default function HubLfgModerationPanel({
  post,
  currentUser,
  locale,
  messages = {},
}) {
  const isAdmin = ['admin', 'superadmin'].includes(currentUser?.role || '');
  const isOwner = Boolean(
    currentUser?.userNumber &&
    post?.userNumber &&
    currentUser.userNumber === post.userNumber,
  );
  const canModerate = Boolean(currentUser && (isOwner || isAdmin));

  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(canModerate);
  const [errorMessage, setErrorMessage] = useState('');
  const [activeRequestKey, setActiveRequestKey] = useState('');

  const pendingCount = useMemo(
    () => requests.filter((request) => request.status === 'pending').length,
    [requests],
  );
  const msg = (path, fallback = '', replacements = {}) =>
    readHubMessage(messages, path, fallback, replacements);

  useEffect(() => {
    let isMounted = true;

    const loadRequests = async () => {
      if (!canModerate) {
        setRequests([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setErrorMessage('');

      try {
        const response = await clientFetch(
          `/api/hub/lfg/${post.lfgNumber}/join-requests`,
        );

        if (!isMounted) {
          return;
        }

        setRequests(Array.isArray(response?.requests) ? response.requests : []);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setErrorMessage(
          error?.data?.message ||
            error?.message ||
            msg(
              'lfgPost.moderation.errors.loadRequests',
              'Unable to load join requests.',
            ),
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadRequests();

    return () => {
      isMounted = false;
    };
  }, [canModerate, post.lfgNumber]);

  if (!canModerate) {
    return null;
  }

  const updateRequestStatus = async (userNumber, status) => {
    const requestKey = `${userNumber}:${status}`;
    setErrorMessage('');
    setActiveRequestKey(requestKey);

    try {
      const response = await clientFetch(
        `/api/hub/lfg/${post.lfgNumber}/join-requests/${encodeURIComponent(userNumber)}`,
        {
          method: 'PATCH',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({ status }),
        },
      );

      setRequests((currentRequests) =>
        currentRequests.map((request) =>
          request.userNumber === userNumber
            ? {
                ...request,
                status: response?.status || status,
                updatedAt: new Date().toISOString(),
              }
            : request,
        ),
      );
    } catch (error) {
      setErrorMessage(
        error?.data?.message ||
          error?.message ||
          msg(
            'lfgPost.moderation.errors.updateStatus',
            'Unable to update join request status.',
          ),
      );
    } finally {
      setActiveRequestKey('');
    }
  };

  return (
    <section
      id="hub-lfg-join"
      className="hub-action-panel hub-action-panel--lfg"
    >
      <div className="hub-action-panel__header">
        <div>
          <p className="hub-section__eyebrow hub-section__eyebrow--info">
            {msg('lfgPost.moderation.eyebrow', 'Host Review')}
          </p>
          <h2>{msg('lfgPost.join.requestsTitle', 'Join requests')}</h2>
        </div>
        <span className="hub-pill hub-pill--warning">
          {msg('lfgPost.moderation.pendingCount', '{{count}} pending', {
            count: pendingCount,
          })}
        </span>
      </div>

      <p className="shop-panel__meta">
        {msg(
          'lfgPost.moderation.hint',
          'Review incoming join requests for this post and update approval status without leaving the page.',
        )}
      </p>

      {errorMessage ? (
        <div className="auth-message auth-message--error">{errorMessage}</div>
      ) : null}

      {isLoading ? (
        <p className="shop-panel__meta">
          {msg(
            'lfgPost.moderation.loadingRequests',
            'Loading join requests...',
          )}
        </p>
      ) : requests.length > 0 ? (
        <div className="hub-request-list">
          {requests.map((request) => {
            const requestLabel =
              request.displayName || request.name || request.userNumber;

            return (
              <article
                key={request.userNumber}
                className="hub-request-card surface-card"
              >
                <div className="hub-request-card__header">
                  <div className="hub-request-card__identity">
                    <h3>{requestLabel}</h3>
                    <p className="product-card__meta">
                      {request.userNumber} •{' '}
                      {msg('lfgPost.moderation.requestedLabel', 'Requested')}{' '}
                      {formatRequestTime(
                        request.createdAt,
                        locale,
                        msg('lfgPost.moderation.unknownTime', 'Unknown time'),
                      )}
                    </p>
                  </div>
                  <span className={`hub-pill${getStatusTone(request.status)}`}>
                    {msg(
                      `lfgPost.join.status.${request.status || 'pending'}`,
                      request.status || 'pending',
                    )}
                  </span>
                </div>

                {request.contact ? (
                  <p className="product-card__meta">
                    <strong>
                      {msg('lfgPost.join.requestContactLabel', 'Their contact')}
                      :
                    </strong>{' '}
                    {request.contact}
                  </p>
                ) : null}

                {request.message ? (
                  <FormField
                    as="textarea"
                    label={msg(
                      'lfgPost.join.requestMessageLabel',
                      'Their message',
                    )}
                    rows={3}
                    value={request.message}
                    readOnly
                    hideLabel
                    inputClassName="ps-3"
                    fieldClassName="auth-field"
                    controlWrapClassName="mt-2"
                  />
                ) : (
                  <p className="shop-panel__meta">
                    {msg(
                      'lfgPost.moderation.noMessage',
                      'No message was included with this request.',
                    )}
                  </p>
                )}

                <div className="auth-form__actions">
                  <button
                    type="button"
                    className="app-button app-button--primary app-button--compact"
                    onClick={() =>
                      updateRequestStatus(request.userNumber, 'approved')
                    }
                    disabled={Boolean(activeRequestKey)}
                  >
                    {activeRequestKey === `${request.userNumber}:approved`
                      ? msg('lfgPost.moderation.approvingCta', 'Approving...')
                      : msg('lfgPost.join.approveCta', 'Approve')}
                  </button>
                  <button
                    type="button"
                    className="app-button app-button--ghost app-button--compact"
                    onClick={() =>
                      updateRequestStatus(request.userNumber, 'rejected')
                    }
                    disabled={Boolean(activeRequestKey)}
                  >
                    {activeRequestKey === `${request.userNumber}:rejected`
                      ? msg('lfgPost.moderation.rejectingCta', 'Rejecting...')
                      : msg('lfgPost.join.rejectCta', 'Decline')}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <p className="shop-panel__meta">
          {msg('lfgPost.join.requestsEmpty', 'No join requests yet.')}
        </p>
      )}
    </section>
  );
}
